import { nanoid } from "nanoid";
import { Fragment } from "react/jsx-runtime";
import { formatPrice } from "../../../utils/formatPrice";
import { convertToFormattedAmount } from "../../../utils/convertAmount";
import type { MortgagePayloadInterface } from "../../../interfaces/mortgageDetailsInterface";
import {
  Box,
  Paper,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Typography,
  TableContainer
} from "@mui/material";

interface CreditConsentSectionProps {
  mortgageDetails: MortgagePayloadInterface;
}

interface RowField {
  label: string;
  value: number | undefined | null;
  isCurrency?: boolean;
}

interface Section {
  section: string;
  fields: RowField[];
}

export const MortgageCalculationsSection = ({
  mortgageDetails
}: CreditConsentSectionProps) => {
  const renderRow = (
    label: string,
    value: number | undefined | null,
    isCurrency = true
  ) => (
    <TableRow hover key={nanoid()}>
      <TableCell>
        <Typography>{label}</Typography>
      </TableCell>
      <TableCell>
        <Typography color={value ? "primary" : "textSecondary"}>
          {value
            ? isCurrency
              ? `$${formatPrice(convertToFormattedAmount(value))}`
              : value
            : "N/A"}
        </Typography>
      </TableCell>
    </TableRow>
  );

  const { mortgage } = mortgageDetails;

  const rows: Section[] = [
    {
      section: "Affordability",
      fields: [
        {
          label: "Maximum Affordability",
          value: mortgage.maximumAffordability?.amount
        },
        { label: "Zown Contribution", value: mortgage.zownContribution?.amount }
      ]
    },
    {
      section: "Credit Score",
      fields: [
        {
          label: "Credit Score",
          value: mortgage.creditScore,
          isCurrency: false
        },
        {
          label: "Highest Eligible Credit Score",
          value: mortgage.highestEligibleCreditScore,
          isCurrency: false
        }
      ]
    },
    {
      section: "Co-Applicant",
      fields: [
        {
          label: "Total Down Payment",
          value: mortgage.totalDownPaymentBuyerOnly?.amount
        },
        {
          label: "Total Down Payment Needed",
          value: mortgage.totalDownPaymentNeeded?.amount
        },
        {
          label: "New Down Payment Needed",
          value: mortgage.totalNewDownPaymentNeeded?.amount
        },

        {
          label: "Total Annual Income Buyer Only",
          value: mortgage.totalAnnualIncomeBuyerOnly?.amount
        },
        {
          label: "Total Annual Income (With Co-Applicants)",
          value: mortgage.totalAnnualIncomeWithCoApplicants?.amount
        },
        {
          label: "Total Monthly Debt Buyer Only",
          value: mortgage.totalMonthlyDebtBuyerOnly?.amount
        },
        {
          label: "Total Debt Buyer Only",
          value: mortgage.totalDebtBuyerOnly?.amount
        },
        {
          label: "Total Monthly Debt (With Co-Applicants)",
          value: mortgage.totalMonthlyDebtWithCoApplicants?.amount
        },
        {
          label: "Total Debt (With Co-Applicants)",
          value: mortgage.totalDebtWithCoApplicants?.amount
        }
      ]
    }
  ];

  return (
    <Paper elevation={4} sx={{ p: 2, mt: 2, flex: "1 1 calc(50% - 16px)" }}>
      <Box sx={{ mb: 3, textAlign: "center" }}>
        <Typography variant="h5" color="primary" fontWeight="bold">
          Summary
        </Typography>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle2" color="textSecondary">
                  Details
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" color="textSecondary">
                  Amount
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((section) => (
              <Fragment key={nanoid()}>
                {section.fields.map(({ label, value, isCurrency }) =>
                  renderRow(label, value, isCurrency ?? true)
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
